import { z } from "zod"
import { marketplaceIdSchema } from "../marketplace-id"
import { isoDate, masterCategorySchema, roleSchema } from "./primitives"

// ── User ───────────────────────────────────────────────────────────────────

export const userSchema = z.object({
  id: z.number().int(),
  email: z.string().nullable(),
  role: roleSchema,
  active: z.boolean(),
  name: z.string().nullable(),
  createdAt: isoDate,
})
export type User = z.infer<typeof userSchema>

export const updateUserRoleBodySchema = z.object({
  role: roleSchema,
})
export type UpdateUserRoleBody = z.infer<typeof updateUserRoleBodySchema>

// ── QA-cron eval results ───────────────────────────────────────────────────

// A calendar date in `YYYY-MM-DD`. The eval grid is keyed by night, so the date
// is the read/write key for one run of the grid — not a timestamp.
const qaEvalDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD")

// One (cell, dimension) result the QA cron writes. Mirrors the repo's
// `QaEvalResultInput`: a cell is one active writer — a (marketplace,
// marketplace-category) pair — rolled up for display by (marketplace,
// master_category). Gate dimensions set `pass`; the single scored fluency axis
// sets `score`; `judgeSpread` records N-vote disagreement on judged dimensions.
export const qaEvalResultInputSchema = z
  .object({
    evalDate: qaEvalDateSchema,
    // The committed writer identity — the eval cell's stable key across nights.
    writerKey: z.string().min(1),
    marketplace: marketplaceIdSchema,
    masterCategory: masterCategorySchema,
    dimension: z.string().min(1),
    pass: z.boolean().nullable().optional(),
    // The 1–5 fluency scale is fixed by the design; only the absolute pass-floor
    // (which score counts as "passing") is calibrated later, not the scale itself.
    score: z.number().min(1).max(5).nullable().optional(),
    codes: z.array(z.string()),
    // A judge-vote spread can't be negative.
    judgeSpread: z.number().min(0).nullable().optional(),
  })
  .strict()
export type QaEvalResultInputBody = z.infer<typeof qaEvalResultInputSchema>

// POST /api/qa/eval-results — the cron writes one night's per-cell results.
export const qaEvalResultsBodySchema = z.array(qaEvalResultInputSchema)
export type QaEvalResultsBody = z.infer<typeof qaEvalResultsBodySchema>

// Optional body for POST /api/qa/precheck — override the committed fixture path
// (tests/diagnostics); absent ⇒ the default golden-products fixture.
export const qaPrecheckBodySchema = z
  .object({
    fixturePath: z.string().min(1).optional(),
  })
  .strict()
export type QaPrecheckBody = z.infer<typeof qaPrecheckBodySchema>

// ── Error envelope ─────────────────────────────────────────────────────────

export const apiErrorSchema = z.object({
  error: z.object({
    kind: z.enum([
      "validation",
      "not_found",
      "expired",
      "conflict",
      "illegal_transition",
      "unauthorized",
      "forbidden",
      "upstream",
      "unavailable",
      "network",
      "aborted",
      "unknown",
    ]),
    message: z.string(),
    details: z.unknown().optional(),
  }),
})
export type ApiErrorBody = z.infer<typeof apiErrorSchema>
